import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Eye, EyeOff, ArrowLeft, Sun, Moon } from 'lucide-react';
import { Mascot } from '../components/Mascot';

// Skipya Logo with Robot Mascot
const SkipyaLogo = ({ size = 70 }) => (
  <Mascot size={size} />
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate(next && next.startsWith('/') ? next : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-8 transition-colors duration-300 ${
      isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'
    }`}>
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-[#81d8d0] rounded-full filter blur-[150px] ${isDark ? 'opacity-15' : 'opacity-20'}`} />
        <div className={`absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#4ECDC4] rounded-full filter blur-[120px] ${isDark ? 'opacity-10' : 'opacity-15'}`} />
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 p-3 rounded-xl transition-all duration-300 ${
          isDark 
            ? 'bg-white/5 hover:bg-white/10 text-yellow-400' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back to home */}
        <Link 
          to="/" 
          className={`flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          <span>Retour à l'accueil</span>
        </Link>

        <Card className={`backdrop-blur-xl border transition-colors duration-300 ${
          isDark 
            ? 'bg-[#1A1A2E]/80 border-white/10' 
            : 'bg-white/80 border-gray-200 shadow-xl'
        }`}>
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <SkipyaLogo size={70} />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className={`text-2xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Skipya
              </span>
            </div>
            <CardTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Content de te revoir !
            </CardTitle>
            <CardDescription className={isDark ? 'text-[#94A3B8]' : 'text-gray-500'}>
              Connecte-toi pour accéder à ton dashboard
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Email</label>
                <Input
                  type="email"
                  placeholder="ton@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  className={`transition-colors ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                  }`}
                />
              </div>
              
              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Mot de passe</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                    className={`pr-10 transition-colors ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                      isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-400 hover:text-[#81d8d0]'
                    }`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="text-right -mt-1">
                <Link
                  to="/mot-de-passe-oublie"
                  className="text-sm text-[#81d8d0] hover:text-[#6bc4bc]"
                  data-testid="go-to-forgot-password"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[#FF6B6B] text-sm text-center"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold py-5 rounded-xl transition-all duration-300 hover:translate-y-[-1px] hover:shadow-lg"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Se connecter"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                Pas encore de compte ?{' '}
                <Link 
                  to="/register" 
                  className="text-[#81d8d0] hover:text-[#6bc4bc] font-medium"
                  data-testid="go-to-register"
                >
                  Créer un compte
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;
